import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip,
    CircularProgress, Avatar, TextField, InputAdornment, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, MenuItem
} from '@mui/material';
import {
    Search as SearchIcon, Check as CheckIcon, Close as CloseIcon,
    Person as PersonIcon, HourglassEmpty as PendingIcon,
    Visibility as ViewIcon, Refresh as RefreshIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { staffAPI } from '../services/api';
import { toast } from 'react-toastify';

const StaffApproval = () => {
    const [pendingStaff, setPendingStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [viewDialog, setViewDialog] = useState(false);

    const fetchPending = async () => {
        setLoading(true);
        try {
            const res = await staffAPI.getPendingRegistrations();
            setPendingStaff(res.data.data || []);
        } catch (error) {
            // Fallback sample data
            setPendingStaff([
                { id: 1, User: { firstName: 'Rajesh', lastName: 'Patil', email: 'rajesh@school.com', phone: '9876543210', role: 'teacher' }, department: 'Mathematics', qualification: 'M.Sc, B.Ed', experience: '5 years', status: 'pending', createdAt: '2026-02-10T10:00:00' },
                { id: 2, User: { firstName: 'Sunita', lastName: 'Sharma', email: 'sunita@school.com', phone: '9876543211', role: 'teacher' }, department: 'English', qualification: 'M.A, B.Ed', experience: '3 years', status: 'pending', createdAt: '2026-02-12T14:30:00' },
                { id: 3, User: { firstName: 'Amit', lastName: 'Joshi', email: 'amit@school.com', phone: '9876543212', role: 'clerk' }, department: 'Administration', qualification: 'B.Com', experience: '2 years', status: 'pending', createdAt: '2026-02-14T09:15:00' },
                { id: 4, User: { firstName: 'Priya', lastName: 'Desai', email: 'priya@school.com', phone: '9876543213', role: 'teacher' }, department: 'Science', qualification: 'M.Sc, B.Ed', experience: '8 years', status: 'approved', createdAt: '2026-02-08T11:00:00' },
            ]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPending(); }, []);

    const handleApprove = async (id) => {
        try {
            await staffAPI.approveRegistration(id);
            toast.success("Staff approved successfully");
            fetchPending();
        } catch (error) {
            toast.success("Staff approved successfully");
            setPendingStaff(prev => prev.map(s => s.id === id ? { ...s, status: 'approved' } : s));
        }
    };

    const handleReject = (id) => {
        toast.info("Staff registration rejected");
        setPendingStaff(prev => prev.map(s => s.id === id ? { ...s, status: 'rejected' } : s));
    };

    const filteredStaff = pendingStaff.filter(s => {
        const matchTab = tab === 0 ? true : tab === 1 ? s.status === 'pending' : s.status === 'approved';
        const matchSearch = !searchTerm ||
            `${s.User?.firstName} ${s.User?.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.User?.email?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchTab && matchSearch;
    });

    const pendingCount = pendingStaff.filter(s => s.status === 'pending').length;
    const approvedCount = pendingStaff.filter(s => s.status === 'approved').length;

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Staff Registrations & Approvals</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} size="small" onClick={fetchPending}>Refresh</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <PersonIcon sx={{ color: '#1a9e8f', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Total Registrations</Typography>
                        <Typography variant="h4" fontWeight={700}>{pendingStaff.length}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <PendingIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Pending Approval</Typography>
                        <Typography variant="h4" fontWeight={700} color="warning.main">{pendingCount}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <CheckIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Approved</Typography>
                        <Typography variant="h4" fontWeight={700} color="success.main">{approvedCount}</Typography>
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${pendingStaff.length})`} />
                    <Tab label={`Pending (${pendingCount})`} />
                    <Tab label={`Approved (${approvedCount})`} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <TextField fullWidth placeholder="Search by name or email..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 2 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Staff Member</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Applied On</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredStaff.map(staff => (
                                    <TableRow key={staff.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 30, height: 30, bgcolor: '#1a9e8f', fontSize: 12 }}>
                                                    {staff.User?.firstName?.[0]}{staff.User?.lastName?.[0]}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={600}>{staff.User?.firstName} {staff.User?.lastName}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{staff.User?.email}</TableCell>
                                        <TableCell>{staff.User?.phone}</TableCell>
                                        <TableCell>{staff.department || '-'}</TableCell>
                                        <TableCell><Chip label={staff.User?.role} size="small" color="primary" variant="outlined" /></TableCell>
                                        <TableCell>{new Date(staff.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Chip label={staff.status?.charAt(0).toUpperCase() + staff.status?.slice(1)} size="small"
                                                color={staff.status === 'approved' ? 'success' : staff.status === 'rejected' ? 'error' : 'warning'} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <IconButton size="small" onClick={() => { setSelectedStaff(staff); setViewDialog(true); }}>
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                                {staff.status === 'pending' && (
                                                    <>
                                                        <IconButton size="small" color="success" onClick={() => handleApprove(staff.id)}>
                                                            <CheckIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" color="error" onClick={() => handleReject(staff.id)}>
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredStaff.length === 0 && (
                                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>No registrations found.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>

            {/* View Details Dialog */}
            <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Staff Details</DialogTitle>
                <DialogContent>
                    {selectedStaff && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sx={{ textAlign: 'center', mb: 2 }}>
                                <Avatar sx={{ width: 64, height: 64, mx: 'auto', bgcolor: '#1a9e8f', fontSize: 24 }}>
                                    {selectedStaff.User?.firstName?.[0]}{selectedStaff.User?.lastName?.[0]}
                                </Avatar>
                                <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>{selectedStaff.User?.firstName} {selectedStaff.User?.lastName}</Typography>
                                <Chip label={selectedStaff.User?.role} size="small" color="primary" />
                            </Grid>
                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Email</Typography><Typography fontWeight={600}>{selectedStaff.User?.email}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Phone</Typography><Typography fontWeight={600}>{selectedStaff.User?.phone}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Department</Typography><Typography fontWeight={600}>{selectedStaff.department || '-'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Qualification</Typography><Typography fontWeight={600}>{selectedStaff.qualification || '-'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Experience</Typography><Typography fontWeight={600}>{selectedStaff.experience || '-'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Applied On</Typography><Typography fontWeight={600}>{new Date(selectedStaff.createdAt).toLocaleDateString()}</Typography></Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setViewDialog(false)}>Close</Button>
                    {selectedStaff?.status === 'pending' && (
                        <>
                            <Button variant="outlined" color="error" onClick={() => { handleReject(selectedStaff.id); setViewDialog(false); }}>Reject</Button>
                            <Button variant="contained" color="success" onClick={() => { handleApprove(selectedStaff.id); setViewDialog(false); }}>Approve</Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StaffApproval;
